import { apiClient } from '@/core/api/client';
import { logger } from '@/core/logger';
import { deviceIdService } from '@/core/services';
import Constants from 'expo-constants';
import { Linking, Platform } from 'react-native';
import {
    UAEPassAuthParams,
    UAEPassAuthResult,
    UAEPassConfig,
    UAEPassUser
} from '../types';

function getConfigExtra(): Record<string, unknown> | undefined {
    const config =
        Constants.expoConfig ??
        (Constants as { manifest2?: { extra?: { expoClient?: unknown } } }).manifest2
            ?.extra?.expoClient;
    return (config as { extra?: Record<string, unknown> } | null | undefined)?.extra;
}

function sanitizeCallbackError(message: string): string {
    const isTechnical =
        message.includes('400') || message.toLowerCase().includes('status code');
    return isTechnical ? 'Authentication failed. Please try again.' : message;
}

function mapCallbackUser(uaepass: Record<string, any>): UAEPassUser {
    return {
        uuid: uaepass?.uuid || uaepass?.sub || '',
        userType: uaepass?.userType || '',
        fullnameEN: uaepass?.fullnameEN || '',
        fullnameAR: uaepass?.fullnameAR || '',
        firstnameEN: uaepass?.firstnameEN || '',
        firstnameAR: uaepass?.firstnameAR || '',
        lastnameEN: uaepass?.lastnameEN || '',
        lastnameAR: uaepass?.lastnameAR || '',
        nationalityEN: uaepass?.nationalityEN || '',
        nationalityAR: uaepass?.nationalityAR || '',
        gender: uaepass?.gender || '',
        mobile: uaepass?.mobile || '',
        email: uaepass?.email || '',
        idType: 'EID',
        idn: uaepass?.idn || '',
        spuuid1: uaepass?.spuuid || '',
        titleEN: uaepass?.titleEN,
        titleAR: uaepass?.titleAR,
        unifiedId: uaepass?.unifiedId,
        profileType: uaepass?.profileType,
    };
}

function mapCallbackResult(result: any): UAEPassAuthResult {
    const topLevelStatus = result?.status;
    const details = result?.details ?? {};
    const detailsStatus = details?.status;
    const isSuccess = topLevelStatus === 'success' || detailsStatus === 'success';

    if (!isSuccess) {
        const backendError =
            details?.message || result?.message || result?.error || 'Callback processing failed';
        return { success: false, error: sanitizeCallbackError(backendError) };
    }

    const jwtToken = details?.jwt_token;
    return {
        success: true,
        user: mapCallbackUser(details?.uaepass_details ?? {}),
        token: jwtToken || undefined,
        details: result?.details || undefined,
    };
}

function readEnvVar(key: string, staticVal?: string): string {
    const extra = getConfigExtra();
    const fromExtra = extra?.[key];
    const fromDynamic = process.env[key];
    if (typeof staticVal === 'string' && staticVal.length > 0) return staticVal;
    if (typeof fromDynamic === 'string' && fromDynamic.length > 0) return fromDynamic;
    if (typeof fromExtra === 'string' && fromExtra.length > 0) return fromExtra;
    return '';
}

// expo-check-installed-apps only works on native platforms, not web
let checkInstalledApps: any = null;
if (Platform.OS !== 'web') {
    try {
        const checkInstalledAppsModule = require('expo-check-installed-apps');
        checkInstalledApps = checkInstalledAppsModule.checkInstalledApps;
    } catch (error) {
        // Module not available
        logger.warn('UAEPassService', 'expo-check-installed-apps not available', error);
    }
}

class UAEPassService {
    private config: UAEPassConfig;
    private baseUrl: string;
    private appScheme: string;
    private appInstalledCheckScheme: string;
    private androidPackageId: string;
    private isAppInstalled: boolean = false;

    constructor() {
        // Initialize with environment variables
        // For web platform, use web redirect URI; for mobile, use deep link
        const isWeb = Platform.OS === 'web';
        
        const mobClientId = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_CLIENT_ID_MOB',
            process.env.EXPO_PUBLIC_UAE_PASS_CLIENT_ID_MOB,
        );
        const defaultClientId = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_CLIENT_ID',
            process.env.EXPO_PUBLIC_UAE_PASS_CLIENT_ID,
        );
        const clientId = (!isWeb && mobClientId) ? mobClientId : defaultClientId;

        const mobRedirectUri = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_REDIRECT_URI_MOB',
            process.env.EXPO_PUBLIC_UAE_PASS_REDIRECT_URI_MOB,
        );
        const defaultRedirectUri = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_REDIRECT_URI',
            process.env.EXPO_PUBLIC_UAE_PASS_REDIRECT_URI,
        );
        const mobileRedirectUri = mobRedirectUri || defaultRedirectUri;
        const redirectUri = isWeb ? this.getWebRedirectUri() : mobileRedirectUri;

        const scope = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_SCOPE',
            process.env.EXPO_PUBLIC_UAE_PASS_SCOPE,
        );
        const baseUrl = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_BASE_URL',
            process.env.EXPO_PUBLIC_UAE_PASS_BASE_URL,
        );
        const acrValues = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_ACR_VALUES',
            process.env.EXPO_PUBLIC_UAE_PASS_ACR_VALUES,
        );

        this.config = {
            clientId,
            redirectUri,
            scope,
            state: this.generateState(),
            baseUrl,
            acrValues,
        };

        // Set URLs from environment variables
        this.baseUrl = this.config.baseUrl;
        this.appScheme = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_SCHEME',
            process.env.EXPO_PUBLIC_UAE_PASS_SCHEME,
        );
        this.appInstalledCheckScheme = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_APP_INSTALLED_CHECK_SCHEME',
            process.env.EXPO_PUBLIC_UAE_PASS_APP_INSTALLED_CHECK_SCHEME,
        );
        this.androidPackageId = readEnvVar(
            'EXPO_PUBLIC_UAE_PASS_ANDROID_PACKAGE_ID',
            process.env.EXPO_PUBLIC_UAE_PASS_ANDROID_PACKAGE_ID,
        );
    }

    /**
     * Check if UAE Pass app is installed on the device
     * Uses expo-check-installed-apps for reliable cross-platform detection
     * Falls back to Linking.canOpenURL for web platform
     */
    async checkAppInstallation(): Promise<boolean> {
        try {
            let installed = false;

            if (Platform.OS === 'web') {
                // Web platform: UAE Pass app is not applicable
                installed = false;
            } else {
                // Native platforms: use expo-check-installed-apps for reliable detection
                if (checkInstalledApps) {
                    const packageNames = this.getPackageNames();

                    if (packageNames.length > 0) {
                        const installedAppsResult = await checkInstalledApps(packageNames);

                        // Check if any of the UAE Pass apps are installed
                        installed = Object.values(installedAppsResult).some(isInstalled => isInstalled);
                    }
                }

                // Fallback to scheme check (iOS only)
                if (!installed && Platform.OS === 'ios' && this.appScheme) {
                    installed = await Linking.canOpenURL(this.appScheme);
                }
            }

            this.isAppInstalled = installed;
            logger.info('UAEPassService', `App installation status: ${installed ? 'INSTALLED' : 'NOT INSTALLED'}`);
            return installed;
        } catch (error) {
            logger.error('UAEPassService', 'Error checking app installation', error);

            // Fallback to scheme check on error (iOS only)
            try {
                if (Platform.OS === 'ios' && this.appScheme) {
                    const fallbackInstalled = await Linking.canOpenURL(this.appScheme);
                    this.isAppInstalled = fallbackInstalled;
                    logger.info('UAEPassService', `App installation fallback status: ${fallbackInstalled ? 'INSTALLED' : 'NOT INSTALLED'}`);
                    return fallbackInstalled;
                }
                // Android: no scheme fallback available
                this.isAppInstalled = false;
                return false;
            } catch (fallbackError) {
                logger.error('UAEPassService', 'Fallback installation check failed', fallbackError);
                this.isAppInstalled = false;
                return false;
            }
        }
    }

    /**
     * Get UAE Pass package names/schemes for different platforms
     * Returns array of package identifiers to check based on platform
     * Only uses configured environment variables, no static fallbacks
     */
    private getPackageNames(): string[] {
        const packageNames: string[] = [];

        if (Platform.OS === 'android') {
            // Android: use package names from environment variable
            if (this.androidPackageId) {
                packageNames.push(this.androidPackageId);
            }
        } else if (Platform.OS === 'ios') {
            // iOS: use URL schemes from environment variable
            if (this.appScheme) {
                packageNames.push(this.appScheme);
            }
        }

        return packageNames;
    }

    /**
     * Get web redirect URI dynamically from current domain
     * Falls back to environment variable if provided, otherwise uses current origin + /uaepass
     */
    private getWebRedirectUri(): string {
        // Allow override via environment variable
        if (process.env.EXPO_PUBLIC_UAE_PASS_WEB_REDIRECT_URI) {
            return process.env.EXPO_PUBLIC_UAE_PASS_WEB_REDIRECT_URI;
        }

        // Dynamically construct from current domain
        if (typeof window !== 'undefined' && window.location) {
            const origin = window.location.origin;
            const path = '/uaepass';
            const redirectUri = `${origin}${path}`;
            logger.info('UAEPassService', 'Using dynamic web redirect URI', { redirectUri });
            return redirectUri;
        }

        // Fallback (should not happen on web, but for safety)
        logger.warn('UAEPassService', 'Unable to determine web redirect URI, using fallback');
        return process.env.EXPO_PUBLIC_UAE_PASS_REDIRECT_URI || '';
    }

    /**
     * Generate a secure random state parameter for OAuth
     */
    private generateState(): string {
        return Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
    }

    /**
     * Get appropriate ACR values based on app installation status
     * Following UAE Pass mobile integration documentation
     */
    private getAcrValues(): string {
        if (this.isAppInstalled) {
            // App installed: use mobile on-device authentication
            return process.env.EXPO_PUBLIC_UAE_PASS_ACR_VALUES || 'urn:digitalid:authentication:flow:mobileondevice';
        } else {
            // App not installed: use web-based authentication
            return process.env.EXPO_PUBLIC_UAE_PASS_ACR_VALUES_WEB || 'urn:safelayer:tws:policies:authentication:level:low';
        }
    }

    /**
     * Build authorization URL for UAE Pass authentication
     * Using official UAE Pass authorization endpoint
     * Reference: https://docs.uaepass.ae/feature-guides/authentication/web-application/1.-obtaining-the-oauth2-access-code
     */
    private buildAuthUrl(params: UAEPassAuthParams, uiLocale?: string): string {
        const queryParams = new URLSearchParams({
            client_id: params.clientId,
            redirect_uri: params.redirectUri,
            scope: params.scope,
            state: params.state,
            acr_values: params.acrValues,
            response_type: params.responseType
        });

        // Add UI locale for web (en or ar) - optional parameter
        if (uiLocale && (uiLocale === 'en' || uiLocale === 'ar')) {
            queryParams.append('ui_locales', uiLocale);
        }

        return `${this.config.baseUrl}/idshub/authorize?${queryParams.toString()}`;
    }

    /**
     * Main authentication method
     * Web: Uses OAuth2 redirect flow (browser-based)
     * Mobile: Uses WebView with dynamic ACR values based on app installation
     */
    async authenticate(): Promise<UAEPassAuthResult> {
        try {
            if (Platform.OS === 'web') {
                // Web platform: Use OAuth2 redirect flow
                return await this.authenticateViaRedirect();
            } else {
                // Mobile platforms: Use WebView with dynamic ACR values
                // Ensure app installation status is current before authentication
                await this.checkAppInstallation();
                return await this.authenticateViaWebView();
            }
        } catch (error) {
            logger.error('UAEPassService', 'Authentication error', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Authentication failed'
            };
        }
    }

    /**
     * Authenticate via OAuth2 redirect flow (Web only)
     * Following UAE Pass web integration documentation:
     * https://docs.uaepass.ae/feature-guides/authentication/web-application/1.-obtaining-the-oauth2-access-code
     */
    private async authenticateViaRedirect(): Promise<UAEPassAuthResult> {
        try {
            const authParams: UAEPassAuthParams = {
                clientId: this.config.clientId,
                redirectUri: this.config.redirectUri,
                scope: this.config.scope,
                state: this.config.state,
                acrValues: process.env.EXPO_PUBLIC_UAE_PASS_ACR_VALUES_WEB || 'urn:safelayer:tws:policies:authentication:level:low',
                responseType: 'code'
            };

            // Get UI locale from browser language or environment variable (en or ar)
            let uiLocale: string | undefined;
            if (typeof window !== 'undefined') {
                const browserLang = navigator.language || (navigator as any).userLanguage;
                uiLocale = browserLang.startsWith('ar') ? 'ar' : 'en';
            }
            // Allow override via environment variable
            if (process.env.EXPO_PUBLIC_UAE_PASS_UI_LOCALE) {
                uiLocale = process.env.EXPO_PUBLIC_UAE_PASS_UI_LOCALE;
            }

            const authUrl = this.buildAuthUrl(authParams, uiLocale);

            // Store state in sessionStorage for validation after redirect
            if (typeof window !== 'undefined') {
                sessionStorage.setItem('uaepass_state', this.config.state);
                sessionStorage.setItem('uaepass_redirect_uri', this.config.redirectUri);
            }

            logger.info('UAEPassService', 'Redirecting to UAE Pass for web authentication', { authUrl });

            // Redirect to UAE Pass authorization URL
            if (typeof window !== 'undefined') {
                window.location.href = authUrl;
            }

            // Return success - actual result will come via callback URL
            return {
                success: true,
                authUrl,
                requiresRedirect: true
            };
        } catch (error) {
            logger.error('UAEPassService', 'Redirect authentication error', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Redirect authentication failed'
            };
        }
    }



    /**
     * Authenticate via WebView following UAE Pass mobile integration guide
     * Uses dynamic ACR values based on app installation status
     */
    private async authenticateViaWebView(): Promise<UAEPassAuthResult> {
        try {
            const authParams: UAEPassAuthParams = {
                clientId: this.config.clientId,
                redirectUri: this.config.redirectUri,
                scope: this.config.scope,
                state: this.config.state,
                acrValues: this.getAcrValues(), // Dynamic ACR values based on app installation
                responseType: 'code'
            };

            const authUrl = this.buildAuthUrl(authParams);

            // Return auth URL for webview usage
            return {
                success: true,
                authUrl,
                requiresWebView: true
            };
        } catch (error) {
            logger.error('UAEPassService', 'WebView authentication error', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'WebView authentication failed'
            };
        }
    }

    /**
     * Handle UAE Pass callback with code and state
     * Makes POST request to backend callback endpoint
     */
    async handleCallback(code: string, state: string): Promise<UAEPassAuthResult> {
        try {
            // TEMP: Bypass backend callback and return mocked UAE Pass data when enabled
            // if (process.env.EXPO_PUBLIC_UAE_PASS_MOCK === 'true') {
            //     const mockUser: UAEPassUser = {
            //         uuid: '5a3a8670-f08d-47bd-8df0-9ed1b94cff28',
            //         userType: 'SOP2',
            //         fullnameEN: 'Suvajit Sarkar',
            //         fullnameAR: 'حكومة مطلعة جيدا',
            //         firstnameEN: 'Suvajit',
            //         firstnameAR: 'سوفاجيت',
            //         lastnameEN: 'Sarkar',
            //         lastnameAR: 'ساركار',
            //         nationalityEN: 'ARE',
            //         nationalityAR: 'اماراتى',
            //         gender: 'Male',
            //         mobile: '917003480487',
            //         email: 'suvajit.sarkar+mock@mavonic.com',
            //         idType: 'EID',
            //         idn: '784-5123-7113391-3',
            //         spuuid1: '1234567',
            //     };
            //     return { success: true, user: mockUser };
            // }

            this.validateCallbackState(state);

            // Get actual device ID
            const deviceId = await deviceIdService.getDeviceId();

            // Determine platform: "web" or "mob" (mobile)
            const platform = Platform.OS === 'web' ? 'web' : 'mob';

            const callbackData = {
                code,
                state,
                device_id: deviceId,
                platform,
                tnc_version: "1.0" // Include T&C version for compliance tracking
            };

            // Post to backend API callback endpoint (not the app redirect URI)
            const { Endpoints } = await import('@/core/api/endpoints');
            const callbackPath = Endpoints.auth.uaePassCallback;

            const response = await apiClient.post<any, typeof callbackData>(callbackPath, callbackData);

            const result = response as any;
            logger.debug('UAEPassService', 'Callback result', result);
            return mapCallbackResult(result);
        } catch (error) {
            logger.error('UAEPassService', 'Callback error', error);
            const rawMessage = error instanceof Error ? error.message : 'Callback processing failed';
            return {
                success: false,
                error: sanitizeCallbackError(rawMessage),
            };
        }
    }

    /**
     * Validate state parameter for security.
     * On web, state is stored in sessionStorage; on mobile, it's in config.
     */
    private validateCallbackState(state: string): void {
        if (Platform.OS === 'web') {
            if (typeof window !== 'undefined') {
                const storedState = sessionStorage.getItem('uaepass_state');
                if (storedState && storedState !== state) {
                    throw new Error('Invalid state parameter');
                }
                sessionStorage.removeItem('uaepass_state');
                sessionStorage.removeItem('uaepass_redirect_uri');
            }
            return;
        }

        if (this.config.state && state && state !== this.config.state) {
            // Mobile: log warning if mismatched due to Activity recreate
            logger.warn('UAEPassService', 'State parameter mismatch on mobile', {
                expected: this.config.state,
                received: state,
            });
        }
    }

    /**
     * Get user profile from UAE Pass using official userinfo endpoint
     */
    async getUserProfile(accessToken: string): Promise<UAEPassUser> {
        try {
            // Temporarily pass token via config until apiClient supports per-call token override
            return await apiClient.get<UAEPassUser>(
                `${this.config.baseUrl}/idshub/userinfo`,
                { headers: { 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' } }
            );
        } catch (error) {
            logger.error('UAEPassService', 'Get user profile error', error);
            throw new Error('Failed to get user profile');
        }
    }


    /**
     * Get the current app installation status
     */
    getAppInstallationStatus(): boolean {
        return this.isAppInstalled;
    }

    /**
     * Get the current configuration
     */
    getConfig(): UAEPassConfig {
        return { ...this.config };
    }

    /**
     * Update configuration (useful for testing)
     */
    updateConfig(newConfig: Partial<UAEPassConfig>): void {
        this.config = { ...this.config, ...newConfig };

        // Update base URL if changed
        if (newConfig.baseUrl) {
            this.baseUrl = newConfig.baseUrl;
        }
    }
}

// Export singleton instance
export const uaePassService = new UAEPassService();
export default uaePassService;